import React, { useState } from 'react'
import { cn } from 'cn'
import { TruckIcon, MagnifyingGlassIcon } from '@heroicons/react/24/solid'
import Image from 'components/Image'

import Link from 'next/link'
import useTheme from 'lib/theme/useTheme'
import MenuItem from './components/common/MenuItem'
import MenuTransition from './components/desktop/MenuTransition'
import SubMenuItemsCustomers from './components/common/SubMenuItemsCustomers'
import SubMenuItemsTrucks from './components/common/SubMenuItemsTrucks'
import SubMenuItemsProfile from './components/common/SubMenuItemsProfile'
import { useScrollPosition } from './useScrollPosition'
import CartComponent from './CartComponent'
import { useHeaderConfig } from './HeaderProvider/useHeaderConfig'
import ForkIcon from 'components/icons/ForkIcon'

type HeaderContainerProps = {
  children: React.JSX.Element | React.JSX.Element[]
  narrowHeader: boolean
  transparentHeader: boolean
}
const HeaderContainer = ({
  children,
  narrowHeader,
  transparentHeader
}: HeaderContainerProps) => {
  const { theme } = useTheme()
  const themedHeader = theme.page.themedBackground

  return (
    <div
      className={cn(
        'fixed w-full hidden lg:block z-30 transition-all duration-200 ease-in-out',
        {
          'h-20': !narrowHeader,
          'h-16': narrowHeader,
          'bg-white shadow-sm border-b border-gray-200/25':
            !transparentHeader && !themedHeader,
          'shadow-sm bg-theme-primary text-theme-primary-text font-theme-body':
            !transparentHeader && themedHeader,
          'bg-transparent text-white border-b border-solid border-white/25':
            transparentHeader
        }
      )}
    >
      <div
        className={cn(
          'flex justify-between items-center max-w-7xl mx-auto px-4 py-4',
          {
            'h-20': !narrowHeader,
            'h-16': narrowHeader
          }
        )}
      >
        {children}
      </div>
    </div>
  )
}

type Props = {
  headerAction?: React.ReactNode
  transparentOnTop: boolean
  highlightCartItems: boolean
  hideNavigation: boolean
}

export default function DesktopHeader({
  headerAction,
  transparentOnTop = false,
  hideNavigation,
  highlightCartItems
}: Props) {
  const { logoUrl, user } = useHeaderConfig()
  const { theme } = useTheme()
  const [transparentHeader, setTransparentHeader] =
    useState<boolean>(transparentOnTop)
  const [narrowHeader, setNarrowHeader] = useState<boolean>(false)

  useScrollPosition(
    ({ currPos }) => {
      if (currPos.y === 0) {
        setTransparentHeader(transparentOnTop)
        setNarrowHeader(false)
      } else {
        setNarrowHeader(true)
        setTransparentHeader(false)
      }
    },
    [transparentHeader, narrowHeader]
  )

  return (
    <HeaderContainer
      narrowHeader={narrowHeader}
      transparentHeader={transparentHeader}
    >
      <>
        {!hideNavigation ? (
          <>
            <div className="lg:w-20 xl:w-28 shrink-0">
              <Link className="flex-none" href="/" prefetch={false}>
                <Image
                  alt="BFT logo"
                  className="mb-2"
                  layout="responsive"
                  width={112}
                  height={64}
                  src={logoUrl}
                />
              </Link>
            </div>

            <nav className="flex items-center mx-auto gap-x-6">
              <MenuItem
                href="/food-trucks-near-me"
                Icon={TruckIcon}
                title="Trucks near me"
                transparentHeader={transparentHeader}
              />
              <MenuItem
                href="/food-trucks-search"
                Icon={MagnifyingGlassIcon}
                title="Search"
                transparentHeader={transparentHeader}
              />
              <MenuItem
                href="/food-trucks/catering"
                Icon={ForkIcon}
                title="Catering"
                transparentHeader={transparentHeader}
              />
              <MenuTransition
                title="Customers"
                transparentHeader={transparentHeader}
              >
                <SubMenuItemsCustomers />
              </MenuTransition>

              <MenuTransition
                title="Truck owners"
                transparentHeader={transparentHeader}
              >
                <SubMenuItemsTrucks />
              </MenuTransition>
            </nav>
          </>
        ) : (
          headerAction
        )}

        <div
          className={cn('flex items-center justify-end md:ml-10', {
            'flex-1': hideNavigation
          })}
        >
          {user ? (
            <MenuTransition
              title="My Profile"
              transparentHeader={transparentHeader}
            >
              <SubMenuItemsProfile user={user} />
            </MenuTransition>
          ) : (
            <MenuItem
              href="/customers/login"
              title="Login / Sign up"
              transparentHeader={transparentHeader}
            />
          )}
          {!theme.page.hideCart && (
            <CartComponent highlightCartItems={highlightCartItems} />
          )}
        </div>
      </>
    </HeaderContainer>
  )
}
